// pages/CrearCertificacion.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import '../styles/CrearCertificacion.css';
import { useFiscal } from '../hooks/useFiscal';
import { obtenerFechaLocal } from '../utils/dateUtils';

const CrearCertificacion = () => {
  const fiscal = useFiscal();
  const { proyectoId } = useParams();
  const navigate = useNavigate();

  const [proyecto, setProyecto] = useState(null);
  const [partidasPresupuesto, setPartidasPresupuesto] = useState([]);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);

  // Datos del formulario
  const [fecha, setFecha] = useState(obtenerFechaLocal());
  const [periodoInicio, setPeriodoInicio] = useState('');
  const [periodoFin, setPeriodoFin] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [usarPresupuesto, setUsarPresupuesto] = useState(true);

  // Partidas seleccionadas
  const [partidasSeleccionadas, setPartidasSeleccionadas] = useState([]);
  const [vistaPartidas, setVistaPartidas] = useState('tarjetas');

  // Para partidas manuales
  const [partidaManual, setPartidaManual] = useState({
    codigo: '',
    descripcion: '',
    unidad: 'glb',
    cantidad_ejecutada: '',
    precio_unitario: ''
  });

  useEffect(() => {
    cargarDatos();
  }, [proyectoId]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [resProyecto, resPartidas] = await Promise.all([
        api.get(`/proyectos/${proyectoId}`),
        api.get(`/proyectos/${proyectoId}/partidas-disponibles`)
      ]);

      setProyecto(resProyecto.data);
      setPartidasPresupuesto(resPartidas.data.partidas || []);

      // Si no hay partidas de presupuesto, forzar modo manual
      if (!resPartidas.data.partidas || resPartidas.data.partidas.length === 0) {
        setUsarPresupuesto(false);
      }
    } catch (error) {
      console.error('Error cargando datos:', error);
      alert('Error al cargar datos del proyecto');
    } finally {
      setLoading(false);
    }
  };

  const formatearMoneda = (valor) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: fiscal.moneda?.codigo || 'DOP'
    }).format(valor || 0);
  };

  const handleAgregarPartidaPresupuesto = (partidaPresupuesto) => {
    // Verificar si ya está agregada
    if (partidasSeleccionadas.find(p => p.PartidaPresupuestoId === partidaPresupuesto.id)) {
      alert('Esta partida ya está agregada');
      return;
    }

    // Verificar si tiene cantidad pendiente
    if (partidaPresupuesto.cantidad_pendiente <= 0) {
      alert('Esta partida ya está 100% certificada');
      return;
    }

    const nuevaPartida = {
      PartidaPresupuestoId: partidaPresupuesto.id,
      codigo: partidaPresupuesto.codigo,
      descripcion: partidaPresupuesto.descripcion,
      unidad: partidaPresupuesto.unidad,
      cantidad_total: partidaPresupuesto.cantidad,
      cantidad_ejecutada: partidaPresupuesto.cantidad_pendiente,
      precio_unitario: partidaPresupuesto.precio_unitario,
      monto_anterior: partidaPresupuesto.monto_certificado,
      // Calcular valores
      porcentaje_ejecutado: 0,
      monto_certificado: 0
    };

    // Calcular monto y porcentaje
    calcularValoresPartida(nuevaPartida);

    setPartidasSeleccionadas([...partidasSeleccionadas, nuevaPartida]);
  };

  const handleAgregarPartidaManual = () => {
    // Validaciones
    if (!partidaManual.descripcion.trim()) {
      alert('La descripción es requerida');
      return;
    }

    if (!partidaManual.cantidad_ejecutada || parseFloat(partidaManual.cantidad_ejecutada) <= 0) {
      alert('La cantidad ejecutada debe ser mayor a 0');
      return;
    }

    if (!partidaManual.precio_unitario || parseFloat(partidaManual.precio_unitario) <= 0) {
      alert('El precio unitario debe ser mayor a 0');
      return;
    }

    const nuevaPartida = {
      PartidaPresupuestoId: null,
      codigo: partidaManual.codigo || null,
      descripcion: partidaManual.descripcion,
      unidad: partidaManual.unidad,
      cantidad_total: null,
      cantidad_ejecutada: parseFloat(partidaManual.cantidad_ejecutada),
      precio_unitario: parseFloat(partidaManual.precio_unitario),
      monto_anterior: 0,
      porcentaje_ejecutado: null,
      monto_certificado: parseFloat(partidaManual.cantidad_ejecutada) * parseFloat(partidaManual.precio_unitario)
    };

    setPartidasSeleccionadas([...partidasSeleccionadas, nuevaPartida]);

    // Limpiar formulario
    setPartidaManual({
      codigo: '',
      descripcion: '',
      unidad: 'glb',
      cantidad_ejecutada: '',
      precio_unitario: ''
    });
  };

  const calcularValoresPartida = (partida) => {
    const cantidad = parseFloat(partida.cantidad_ejecutada) || 0;
    const precio = parseFloat(partida.precio_unitario) || 0;
    
    partida.monto_certificado = cantidad * precio;

    if (partida.cantidad_total && partida.cantidad_total > 0) {
      partida.porcentaje_ejecutado = (cantidad / partida.cantidad_total) * 100;
    }
  };

  const handleCantidadChange = (index, valor) => {
    const nuevasPartidas = [...partidasSeleccionadas];
    nuevasPartidas[index].cantidad_ejecutada = parseFloat(valor) || 0;
    calcularValoresPartida(nuevasPartidas[index]);
    setPartidasSeleccionadas(nuevasPartidas);
  };

  const handlePrecioChange = (index, valor) => {
    const nuevasPartidas = [...partidasSeleccionadas];
    nuevasPartidas[index].precio_unitario = parseFloat(valor) || 0;
    calcularValoresPartida(nuevasPartidas[index]);
    setPartidasSeleccionadas(nuevasPartidas);
  };

  const handleEliminarPartida = (index) => {
    const nuevasPartidas = partidasSeleccionadas.filter((_, i) => i !== index);
    setPartidasSeleccionadas(nuevasPartidas);
  };

  const calcularMontoTotal = () => {
    return partidasSeleccionadas.reduce((sum, p) => sum + (p.monto_certificado || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validaciones
    if (!fecha) {
      alert('La fecha es requerida');
      return;
    }

    if (partidasSeleccionadas.length === 0) {
      alert('Debe agregar al menos una partida');
      return;
    }

    // Confirmar
    const montoTotal = calcularMontoTotal();
    if (!window.confirm(`¿Crear certificación por ${formatearMoneda(montoTotal)}?`)) {
      return;
    }

    try {
      setGuardando(true);

      const data = {
        ProyectoId: parseInt(proyectoId),
        fecha,
        periodo_inicio: periodoInicio || null,
        periodo_fin: periodoFin || null,
        observaciones: observaciones || null,
        partidas: partidasSeleccionadas.map(p => ({
          PartidaPresupuestoId: p.PartidaPresupuestoId,
          codigo: p.codigo,
          descripcion: p.descripcion,
          unidad: p.unidad,
          cantidad_total: p.cantidad_total,
          cantidad_ejecutada: p.cantidad_ejecutada,
          porcentaje_ejecutado: p.porcentaje_ejecutado,
          precio_unitario: p.precio_unitario,
          monto_anterior: p.monto_anterior || 0
        }))
      };

      await api.post('/certificaciones', data);

      navigate(`/proyectos/${proyectoId}/certificaciones`);

    } catch (error) {
      console.error('Error creando certificación:', error);
      alert(error.response?.data?.error || 'Error al crear certificación');
    } finally {
      setGuardando(false);
    }
  };

  if (loading) {
    return (
      <div className="crear-cert-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Cargando datos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="crear-cert-container">
      {/* Header */}
      <div className="crear-cert-header">
        <div className="header-info">
          <button
            onClick={() => navigate(`/proyectos/${proyectoId}/certificaciones`)}
            className="btn-back"
          >
            ← Volver a Certificaciones
          </button>
          <h1>Nueva Certificación de Obra</h1>
          <p className="proyecto-nombre">{proyecto?.nombre}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="cert-form">
        {/* Datos Generales */}
        <div className="form-section">
          <h2>📋 Datos Generales</h2>
          
          <div className="form-grid">
            <div className="form-group">
              <label>Fecha de Certificación *</label>
              <input
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                required
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label>Período Inicio</label>
              <input
                type="date"
                value={periodoInicio}
                onChange={(e) => setPeriodoInicio(e.target.value)}
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label>Período Fin</label>
              <input
                type="date"
                value={periodoFin}
                onChange={(e) => setPeriodoFin(e.target.value)}
                className="form-control"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Observaciones</label>
            <textarea
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              className="form-control"
              rows="3"
              placeholder="Observaciones sobre esta certificación..."
            />
          </div>
        </div>

        {/* Toggle Usar Presupuesto */}
        {partidasPresupuesto.length > 0 && (
          <div className="form-section toggle-section">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={usarPresupuesto}
                onChange={(e) => setUsarPresupuesto(e.target.checked)}
              />
              <span>☑ Usar partidas del presupuesto detallado</span>
            </label>
          </div>
        )}

        {/* Agregar Partidas */}
        <div className="form-section">
          <h2>📦 Partidas a Certificar</h2>

          {usarPresupuesto ? (
  <div className="partidas-disponibles-container">
    <div className="partidas-disponibles-header">
      <p className="section-description">
        Selecciona las partidas del presupuesto que deseas certificar:
      </p>
      
      <div className="vista-toggle">
        <button
          type="button"
          className={vistaPartidas === 'tarjetas' ? 'active' : ''}
          onClick={() => setVistaPartidas('tarjetas')}
          title="Vista de tarjetas"
        >
          ▦
        </button>
        <button
          type="button"
          className={vistaPartidas === 'lista' ? 'active' : ''}
          onClick={() => setVistaPartidas('lista')}
          title="Vista de lista"
        >
          ☰
        </button>
      </div>
    </div>

            {vistaPartidas === 'tarjetas' ? (
  /* VISTA DE TARJETAS (la que ya existe) */
  <div className="partidas-disponibles-grid">
    {partidasPresupuesto.map((partida) => (
      <div key={partida.id} className="partida-disponible-card">
        <div className="partida-header-disponible">
          <div className="partida-codigo-nombre">
            <span className="partida-codigo">{partida.codigo || 'N/A'}</span>
            <h4>{partida.descripcion}</h4>
          </div>
          <span 
            className="avance-badge"
            style={{
              backgroundColor: partida.porcentaje_avance >= 100 ? '#10b981' : 
                              partida.porcentaje_avance >= 50 ? '#f59e0b' : '#3b82f6'
            }}
          >
          {parseFloat(partida.porcentaje_avance || 0).toFixed(0)}% EJECUTADO
          </span>
        </div>

        <div className="partida-detalles">
         <div className="detalle-row">
  <span>Total:</span>
  <span>{parseFloat(partida.cantidad || 0).toFixed(2)} {partida.unidad}</span>
</div>
<div className="detalle-row">
  <span>Certificado:</span>
  <span>{parseFloat(partida.cantidad_certificada || 0).toFixed(2)} {partida.unidad}</span>
</div>
<div className="detalle-row">
  <span>Pendiente:</span>
  <span>{parseFloat(partida.cantidad_pendiente || 0).toFixed(2)} {partida.unidad}</span>
</div>
          <div className="detalle-row">
            <span>Precio Unit.:</span>
            <span>{formatearMoneda(partida.precio_unitario)}</span>
          </div>
        </div>

        <button
          type="button"
          onClick={() => handleAgregarPartidaPresupuesto(partida)}
          className="btn-agregar-partida"
          disabled={!partida.puede_certificar}
        >
          {partida.puede_certificar ? '➕ Agregar' : '✅ Completa'}
        </button>
      </div>
    ))}

    {partidasPresupuesto.length === 0 && (
      <div className="empty-message">
        No hay partidas disponibles en el presupuesto.
        Cambia a modo manual para agregar partidas.
      </div>
    )}
  </div>
) : (
  /* VISTA DE LISTA (NUEVA) */
  <div className="partidas-disponibles-tabla">
    {partidasPresupuesto.length === 0 ? (
      <div className="empty-message">
        No hay partidas disponibles en el presupuesto.
        Cambia a modo manual para agregar partidas.
      </div>
    ) : (
      <table className="tabla-partidas-disponibles">
        <thead>
          <tr>
            <th>Código</th>
            <th>Descripción</th>
            <th>Unidad</th>
            <th className="text-right">Total</th>
            <th className="text-right">Certificado</th>
            <th className="text-right">Pendiente</th>
            <th className="text-right">P. Unit.</th>
            <th className="text-center">% Avance</th>
            <th className="text-center">Acción</th>
          </tr>
        </thead>
        <tbody>
          {partidasPresupuesto.map((partida) => (
            <tr key={partida.id}>
              <td>{partida.codigo || 'N/A'}</td>
              <td className="descripcion-cell">{partida.descripcion}</td>
              <td>{partida.unidad}</td>
             <td className="text-right">{parseFloat(partida.cantidad || 0).toFixed(2)}</td>
<td className="text-right">{parseFloat(partida.cantidad_certificada || 0).toFixed(2)}</td>
<td className="text-right">{parseFloat(partida.cantidad_pendiente || 0).toFixed(2)}</td>
              <td className="text-right">{formatearMoneda(partida.precio_unitario)}</td>
              <td className="text-center">
                <span 
                  className="avance-badge-small"
                  style={{
                    backgroundColor: partida.porcentaje_avance >= 100 ? '#10b981' : 
                                    partida.porcentaje_avance >= 50 ? '#f59e0b' : '#3b82f6'
                  }}
                >
                {parseFloat(partida.porcentaje_avance || 0).toFixed(0)}%
                </span>
              </td>
              <td className="text-center">
                <button
                  type="button"
                  onClick={() => handleAgregarPartidaPresupuesto(partida)}
                  className="btn-agregar-small"
                  disabled={!partida.puede_certificar}
                >
                  {partida.puede_certificar ? '➕' : '✅'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
)}     </div>
            
          ) : (
            // Formulario para partidas manuales
            <div className="partida-manual-form">
              <p className="section-description">
                Ingresa las partidas manualmente:
              </p>

              <div className="form-grid">
                <div className="form-group">
                  <label>Código (opcional)</label>
                  <input
                    type="text"
                    value={partidaManual.codigo}
                    onChange={(e) => setPartidaManual({ ...partidaManual, codigo: e.target.value })}
                    className="form-control"
                    placeholder="Ej: CERT-001"
                  />
                </div>

                <div className="form-group">
                  <label>Descripción *</label>
                  <input
                    type="text"
                    value={partidaManual.descripcion}
                    onChange={(e) => setPartidaManual({ ...partidaManual, descripcion: e.target.value })}
                    className="form-control"
                    placeholder="Descripción del trabajo"
                  />
                </div>

                <div className="form-group">
                  <label>Unidad *</label>
                  <select
                    value={partidaManual.unidad}
                    onChange={(e) => setPartidaManual({ ...partidaManual, unidad: e.target.value })}
                    className="form-control"
                  >
                    <option value="glb">Global</option>
                    <option value="m²">m² - Metro cuadrado</option>
                    <option value="m³">m³ - Metro cúbico</option>
                    <option value="ml">ml - Metro lineal</option>
                    <option value="un">un - Unidad</option>
                    <option value="kg">kg - Kilogramo</option>
                    <option value="ton">ton - Tonelada</option>
                    <option value="W">W - Vatio</option>
                    <option value="Wp">Wp - Vatio pico</option>
                    <option value="kW">kW - Kilovatio</option>
                    <option value="kWh">kWh - Kilovatio-hora</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Cantidad Ejecutada *</label>
                  <input
                    type="number"
                    value={partidaManual.cantidad_ejecutada}
                    onChange={(e) => setPartidaManual({ ...partidaManual, cantidad_ejecutada: e.target.value })}
                    className="form-control"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                  />
                </div>

                <div className="form-group">
                  <label>Precio Unitario *</label>
                  <input
                    type="number"
                    value={partidaManual.precio_unitario}
                    onChange={(e) => setPartidaManual({ ...partidaManual, precio_unitario: e.target.value })}
                    className="form-control"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={handleAgregarPartidaManual}
                className="btn-secondary"
              >
                ➕ Agregar Partida
              </button>
            </div>
          )}
        </div>

        {/* Tabla de Partidas Seleccionadas */}
        {partidasSeleccionadas.length > 0 && (
          <div className="form-section">
            <h2>📊 Partidas Seleccionadas ({partidasSeleccionadas.length})</h2>

            <div className="tabla-container">
              <table className="tabla-partidas">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Descripción</th>
                    <th>Unidad</th>
                    <th>Cantidad</th>
                    <th>Precio Unit.</th>
                    {usarPresupuesto && <th>% Ejecutado</th>}
                    <th>Monto</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {partidasSeleccionadas.map((partida, index) => (
                    <tr key={index}>
                      <td>{partida.codigo || '-'}</td>
                      <td className="descripcion-cell">{partida.descripcion}</td>
                      <td>{partida.unidad}</td>
                      <td>
                        <input
                          type="number"
                          value={partida.cantidad_ejecutada}
                          onChange={(e) => handleCantidadChange(index, e.target.value)}
                          className="input-small"
                          step="0.01"
                          min="0"
                          max={partida.cantidad_total || undefined}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={partida.precio_unitario}
                          onChange={(e) => handlePrecioChange(index, e.target.value)}
                          className="input-small"
                          step="0.01"
                          min="0"
                          disabled={!!partida.PartidaPresupuestoId}
                        />
                      </td>
                      {usarPresupuesto && (
                        <td>
                          <span className="porcentaje-badge">
                            {partida.porcentaje_ejecutado ? partida.porcentaje_ejecutado.toFixed(2) : '-'}%
                          </span>
                        </td>
                      )}
                      <td className="monto-cell">{formatearMoneda(partida.monto_certificado)}</td>
                      <td>
                        <button
                          type="button"
                          onClick={() => handleEliminarPartida(index)}
                          className="btn-eliminar"
                          title="Eliminar"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="total-row">
                    <td colSpan={usarPresupuesto ? 6 : 5} className="text-right">
                      <strong>TOTAL A CERTIFICAR:</strong>
                    </td>
                    <td className="total-amount" colSpan="2">
                      <strong>{formatearMoneda(calcularMontoTotal())}</strong>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Botones de Acción */}
        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate(`/proyectos/${proyectoId}/certificaciones`)}
            className="btn-cancel"
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn-submit"
            disabled={guardando || partidasSeleccionadas.length === 0}
          >
            {guardando ? 'Guardando...' : '💾 Crear Certificación'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CrearCertificacion;